// noinspection HtmlUnknownBooleanAttribute

import { Component, Input, OnInit } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { NgbActiveModal, NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import {
  AttachFileDialogResponse,
  ColAction,
  ColActionId,
  FolderField,
  IconType,
  IoniconsFilled,
  ListData,
  NavigationData,
  TableActionData,
  WorkspaceInfo
} from '@pt/pt-models';
import { AttachmentsService, FolderService, WorkspaceService } from '@pt/api-client';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Folder, WorkspaceItem } from '@primatoo-ms-organization';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ColumnType } from 'it/primatoo/openapi/api/org/schemas/common/ColumnType';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';
import { Attachment } from 'it/primatoo/openapi/api/org/schemas/common/Attachment';

@Component({
  selector: 'pt-attach-file-dialog',
  template: `
    <div class='modal-header'>
      <h4 class='modal-title' [innerHTML]='title | async'></h4>
    </div>

    <div class='modal-body'>
      <ul ngbNav #navAttach='ngbNav' class='nav-tabs' [(activeId)]='active' (navChange)='onChangeTab($event)'>
        <li ngbNavItem [ngbNavItem]='AttachFileTab.FILE'>
          <a ngbNavLink>{{ "LABELS.LOAD_FILE" | translate }}</a>
          <ng-template ngbNavContent>
            <div class='choose-file-container flex-column'>
              <div class="folder-navigation-container mb-2">
                <div class="folder-navigation" triggers='manual' placement='bottom'
                     [ngbPopover]='fieldPopContent' [autoClose]="'outside'" #popover='ngbPopover'>
                  <pt-icon class="text-warning cursor-pointer"
                           [iconType]="SVG" [size]="18" [key]="'folder.svg'" (click)="onClickRootFolder()"
                  ></pt-icon>
                  <input type='text' autocomplete='off' class='form-control form-control-sm'
                         [readOnly]='true' [(ngModel)]='currentFolderLabel' (click)='popover.open()'>
                  <pt-icon class="cursor-pointer" [key]='IconArrowDown' (click)="popover.open()"></pt-icon>
                </div>
                <ng-template #fieldPopContent class='p-0'>
                  <pt-resource-navigation
                    [data]='folders'
                    [selectedResourceId]='currentFolderId'
                    (clickClose)='popover.close()'
                    (clickItem)='onSelectFolder($event); popover.close()'>
                  </pt-resource-navigation>
                </ng-template>
              </div>
              <form class='choose-file' *ngIf='currentUploadAttachment === null else CURRENT_UPLOAD_ATTACHMENT'>
                <div class='form-group h-100'>
                  <div class='file-drop-area'>
                    <pt-icon [iconType]='SVG' [key]='"upload.svg"' [size]='50'></pt-icon>
                    <div class='input-msg' *ngIf='!currentFile'>
                      {{ "MESSAGE.DRAG_AND_DROP_FILE" | translate }}
                    </div>
                    <div class='input-msg' *ngIf='currentFile'>{{ currentFile?.name }}</div>
                    <div class='input-btn text-uppercase mt-2'>
                      {{ "BUTTON.BROWSE" | translate }}
                    </div>
                    <input id='file-input' autocomplete='off' class='file-input' type='file' multiple
                           accept='.xlsx,.xlsm,.xls,.doc,.docx,.odt,.txt,.csv,.ods,.pdf,.ppt,.pptx,odp,.jpg,.png,.jpeg,.p7m'
                           (change)='changeListener($event)'>
                    <div *ngIf='errorMessage'>
                      <p class='upload-fail-message'>
                        {{ 'MESSAGE.ATTACH_FAIL' | translate }}
                      </p>
                    </div>
                  </div>
                </div>
              </form>
              <ng-template #CURRENT_UPLOAD_ATTACHMENT>
                <div class='attachment-container'>
                  <div class='attachment-content'>
                    <div class='attachment-item'>
                      <pt-image-renderer
                        [type]='currentUploadAttachment?.attachmentType'
                        [label]='currentUploadAttachment?.name'
                        [ngbTooltip]='currentUploadAttachment?.name'
                      ></pt-image-renderer>
                    </div>
                    <div class='actions-container mt-2'>
                      <button type='button' class='btn btn-primary btn-sm me-2'
                              *ngIf='active === AttachFileTab.FILE'
                              [disabled]='!enabledImport || loading'
                              (click)='saveFromFile()'>
                        {{ 'BUTTON.CHOOSE' | translate }}
                      </button>

                      <button class='btn btn-sm btn-outline-primary d-flex align-items-center'
                              (click)='deleteCurrentUploadAttachment()'>
                        <span>{{ "LABELS.DELETE" | translate }}</span>
                      </button>

                    </div>
                  </div>
                </div>
              </ng-template>
            </div>
          </ng-template>
        </li>

        <li ngbNavItem [ngbNavItem]='AttachFileTab.WORKSPACE' *ngIf='!hideWorkspaceTab'>
          <a ngbNavLink>{{ "LABELS.WORKSPACE" | translate }}</a>
          <ng-template ngbNavContent>
            <pt-attachments-workspace-breadcrumb
              [path]='currentPath'
              (openFolder)='openFolder($event)'
            ></pt-attachments-workspace-breadcrumb>

            <div class='mt-1' style='height: calc(100vh - 400px)'>
              <pt-attachment-workspace-table
                [data]='workspaceResources'
                (clickAction)='clickTableAction($event)'
              ></pt-attachment-workspace-table>
            </div>
          </ng-template>
        </li>
      </ul>

      <div [ngbNavOutlet]='navAttach' class='border border-top-0 p-2'></div>
    </div>

    <div class='modal-footer'>
      <div class='spinner-border spinner-border-sm' role='status' *ngIf='loading'></div>
      <button type='button' class='btn btn-primary btn-sm'
              *ngIf='active === AttachFileTab.FILE'
              [disabled]='!enabledImport || loading'
              (click)='saveFromFile()'>
        {{ 'BUTTON.CHOOSE' | translate }}
      </button>
      <button type='button' class='btn btn-primary btn-sm'
              *ngIf='active === AttachFileTab.WORKSPACE'
              [disabled]='!form?.valid'
              (click)='saveFromWorkspace()'>
        {{ 'BUTTON.CHOOSE' | translate }}
      </button>
      <button ngbAutofocus type='button' class='btn btn-outline-secondary btn-sm'
              [disabled]='loading'
              (click)='onCancel()'>
        {{ 'BUTTON.CANCEL' | translate }}
      </button>
    </div>
  `,
  styleUrls: [`./attach-file-dialog.component.scss`],
})
export class AttachFileDialogComponent implements OnInit {
  @Input() title: Observable<string>;
  @Input() hideWorkspaceTab: boolean;
  @Input() selectedFolderId: number;

  public workspaceResources: ListData<WorkspaceItem>;
  public currentPath: WorkspaceInfo[] = [];

  public errorMessage;
  public enabledImport = false;
  public currentFile: File;
  public currentUploadAttachment: Attachment | null = null;
  public loading: boolean;
  public isCollapsedError = true;

  public SVG = IconType.SVG;
  public IconRoot = IoniconsFilled.folder;
  public IconArrowDown = IoniconsFilled.arrowDown;

  public active = AttachFileTab.FILE;
  public AttachFileTab = AttachFileTab;

  public folders: NavigationData[];
  public currentFolderLabel: string;
  public currentFolderId: number;

  public form: FormGroup;

  private rootId: number;
  private parentId: number;

  constructor(
    public store: Store,
    public activeModal: NgbActiveModal,
    private workspaceService: WorkspaceService,
    private folderService: FolderService,
    private attachmentsService: AttachmentsService
  ) {
  }

  ngOnInit(): void {
    forkJoin({
      folders: this.folderService?.findAllFolders(),
      attachmentFolder: this.workspaceService.getWorkspaceAttachment()
    }).subscribe((res: { folders: Folder[], attachmentFolder: WorkspaceItem }) => {
      if (res.folders) {
        this.folders = res.folders.map(f => NavigationData.folderToNavigationData(f));
      }

      if (res.attachmentFolder) {
        this.currentFolderId = this.selectedFolderId ? this.selectedFolderId : res.attachmentFolder.id;
        this.currentFolderLabel = this.folders?.find((f) => f.id === this.currentFolderId).name;
      }
    });

    forkJoin({
      workspaceRoot: this.workspaceService.getWorkspaceRoot(),
      workspaceColEnabled: this.workspaceService.getWorkspaceColEnabled()
    }).subscribe((res: { workspaceRoot: WorkspaceItem, workspaceColEnabled: ColumnWithLocalization[] }) => {
      if (res.workspaceRoot) {
        this.rootId = res.workspaceRoot.id;
        this.parentId = res.workspaceRoot.id;
      }

      if (res.workspaceColEnabled) {
        this.workspaceResources = {
          ...this.workspaceResources,
          colEnabled: [
            {
              label: res.workspaceColEnabled?.find(c => c.column === FolderField.name)?.label,
              uiAvailable: true,
              uiUserShow: true,
              column: FolderField.name,
              type: ColumnType.TEXT,
              operations: res.workspaceColEnabled?.find(col => col.column === FolderField.name)?.operations
            },
            {
              label: res.workspaceColEnabled?.find(c => c.column === FolderField.createdAt)?.label,
              uiAvailable: true,
              uiUserShow: true,
              column: FolderField.createdAt,
              type: ColumnType.DATE,
              operations: res.workspaceColEnabled?.find(col => col.column === FolderField.createdAt)?.operations
            }
          ]
        };
      }
    });
  }

  loadResources(root?: number) {
    if (root) {
      this.workspaceService.getFolderChildren(root).pipe(
        map((action) => {
          return action.filter(r => r.type === 'FOLDER' || r.type === 'ATTACHMENT');
        })
      ).subscribe(res => {
        this.workspaceResources = {
          ...this.workspaceResources,
          data: res
        };
      });
    } else {
      this.workspaceService.getFolderChildren(this.parentId).pipe(
        map((action) => {
          return action.filter(r => r.type === 'FOLDER' || r.type === 'ATTACHMENT');
        })
      ).subscribe(res => {
        this.workspaceResources = {
          ...this.workspaceResources,
          data: res
        };
      });
    }
  }

  createForm() {
    if (!this.form) {
      this.form = new FormGroup({
        resource: new FormControl(null, Validators.required)
      });
    } else {
      this.form = undefined;
      this.createForm();
    }
  }

  changeListener($event): void {
    this.readThis($event.target);
  }

  readThis(inputValue: any): void {
    this.currentFile = inputValue.files[0] as File;
    this.enabledImport = true;
    this.errorMessage = undefined;
    this.isCollapsedError = true;
    this.uploadAttachment();
  }

  uploadAttachment() {
    const api = this.currentFolderId ?
      this.workspaceService.uploadAttachment(this.currentFile, this.currentFolderId) :
      this.attachmentsService.uploadAttachment(this.currentFile);
    api.subscribe((response) => {
      switch (response.type) {
        case 0:
          this.loading = true;
          break;
        case 4:
          this.currentUploadAttachment = response?.body;
          this.loading = false;
          break;
      }
    }, (error) => {
      this.currentUploadAttachment = null;
      const reason = error?.error?.reason;
      this.errorMessage = reason ? reason : undefined;
      this.loading = false;
    });
  }

  saveFromFile() {
    const result: AttachFileDialogResponse = {
      data: this.currentUploadAttachment,
      fromWorkspace: false,
      selectedFolderId: this.currentFolderId
    };
    this.activeModal.close(result as AttachFileDialogResponse);
  }

  saveFromWorkspace() {
    const form = this.form.getRawValue();
    this.attachmentsService.getAttachmentById(form.resource.id).subscribe((success: Attachment) => {
      const result: AttachFileDialogResponse = {
        data: success,
        fromWorkspace: true,
        selectedFolderId: this.currentFolderId
      };
      this.activeModal.close(result as AttachFileDialogResponse);
    }, () => {
      this.activeModal.close(null);
    })

  }

  deleteCurrentUploadAttachment() {
    this.attachmentsService.delete(this.currentUploadAttachment.id).subscribe(() => {
      this.currentFile = undefined;
      this.currentUploadAttachment = null;
      this.errorMessage = undefined;
      this.enabledImport = false;
      this.isCollapsedError = true;
    }, () => {
      this.currentFile = undefined;
      this.currentUploadAttachment = null;
      this.errorMessage = undefined;
      this.enabledImport = false;
      this.isCollapsedError = true;
    });
  }

  onCancel() {
    this.activeModal.close(false);
  }

  openFolder(resource: WorkspaceInfo) {
    if (resource?.parentId && resource?.parentId !== -1) {
      this.parentId = resource.parentId;
      this.currentPath = this.updatePath(resource, this.currentPath);
      this.loadResources();
    } else {
      this.loadResources(this.rootId);
      this.currentPath = [];
      this.createForm();
    }
  }

  clickTableAction(tableAction: TableActionData<WorkspaceItem>) {
    const row = tableAction.row;
    switch (row.type) {
      case 'FOLDER':
        this.clickFolderAction(row, tableAction.action);
        break;
      case 'ATTACHMENT':
        this.form?.controls?.resource?.setValue(row);
        break;
      default:
        this.form?.controls?.resource?.setValue(row);
    }
  }

  clickFolderAction(row: WorkspaceItem, action: ColAction) {
    switch (action.id) {
      case ColActionId.showDetail:
        this.parentId = row.id;
        this.currentPath.push({
          id: row.id,
          parentId: row.id,
          label: row.name
        });
        this.loadResources();
        this.createForm();
        break;
      case ColActionId.select:
        this.createForm();
        break;
    }
  }

  onChangeTab($event: NgbNavChangeEvent) {
    this.active = $event.nextId;
    if ($event.nextId === AttachFileTab.WORKSPACE) {
      this.currentFile = undefined;
      this.currentUploadAttachment = null;
      this.errorMessage = undefined;
      this.enabledImport = false;
      this.isCollapsedError = true;
      this.loadResources();
      this.createForm();
    }
  }

  onSelectFolder($event: NavigationData) {
    this.currentFolderLabel = $event.name;
    this.currentFolderId = $event.id;
  }

  onClickRootFolder() {
    this.currentFolderId = this.rootId;
    this.currentFolderLabel = this.folders?.find((f) => f.id === this.currentFolderId).name;
  }

  private updatePath(resource: WorkspaceInfo, currentPath: WorkspaceInfo[]) {
    const updatePath = [];
    if (resource && currentPath) {
      currentPath.forEach((value, index) => {
        if (index <= currentPath.indexOf(resource)) {
          updatePath.push(value);
        }
      });
    }
    return updatePath;
  }
}

enum AttachFileTab {
  FILE = 'FILE',
  WORKSPACE = 'WORKSPACE'
}
